import { CircleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Label } from './label';

/**
 * Label, hint and error around one form control. The control must use `id`, and reference `${id}-hint` and
 * `${id}-error` through aria-describedby (see describedBy).
 */
export function FormField({
  id,
  label,
  hint,
  error,
  children,
  className,
  labelAside,
}: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
  labelAside?: ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {labelAside}
      </div>
      {children}
      {hint ? (
        <div id={`${id}-hint`} className="text-xs text-subtle">
          {hint}
        </div>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="flex items-start gap-1.5 text-sm font-medium text-danger">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

export function describedBy(
  id: string,
  { hint, error }: { hint?: boolean; error?: boolean },
): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}
